import { addToast } from '@app/components/various/Toasts';
import {
  getBuyerIdFromCurrentOrder,
  getCurrentOrDefaultProductSortingValue,
  getIsSeller,
  getIsStockTypeSeparationEnabled,
  getOrderDetails,
  getProductFilters,
  getProductsExpandedFiltersStates,
  getProductSortingDefaultValue,
  reloadPage,
  setExpandedProductFilters,
  setProductSortingValue,
  updateFilters,
} from '@app/ducks';
import { getOnlyNeccessaryFilters, getParsedToArrayFilters } from '@app/logic/filters';
import { useEtaDates } from '@app/services/hooks/products/useEtaDates';
import { useProducts } from '@app/services/hooks/products/useProducts';
import { GAProductListItemId, GAProductListItemName, mapProductsToGAItems, useGTM } from '@app/utils/analytics';
import { useCurrentProductFiltersAndSort } from '@app/utils/hooks/useCurrentProductFiltersAndSort';
import { useSyncFiltersSearchQuery } from '@app/utils/hooks/useSyncFiltersSearchQuery';
import { isDefined } from '@app/utils/is';
import { Dates, Filter, Product, ProductFilters } from '@typings';
import { isEmpty, omit } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const NUMBER_OF_FAILED_ATTEMPTS = 3;

interface Context {
  productAttributes: Filter.Field<string>[];
  isLoadingProducts: boolean;
  isLoadingFilters: boolean;
  shouldShowNoResults: boolean;
  sortedProducts: Product.Full[];
  allProductsIds: string[];
  fetchNextPage: VoidFunction;
  isFetchingNextPage: boolean;
  error: Error | null;
  hasNextPage: boolean;
  pageCount: number;
  productsEtaDates: Dates.DateAsSQLString[];
  totalItems: number;
  changeFilters: (newFilters: ProductFilters, preserveRest?: boolean) => void;
  clearFilters: VoidFunction;
  changeSort: (sorting: string) => void;
  filters: ProductFilters;
  sort: string;
  expandedFilters: Record<string, boolean>;
  productsDetails?: Record<string, Product.Full>;
}

export const useAllProductsContext = (): Context => {
  const context = React.useContext(AllProductsContext);

  if (context === null) {
    throw new Error('AllProductsContext can not be used outside the scope of AllProductsContextProvider');
  }

  return context;
};

const AllProductsContext = React.createContext<Context | null>(null);

interface Props {
  orderId: string;
  isAllProductsPage?: boolean;
}

export const AllProductsContextProvider = ({ children, orderId }: React.WithChildren<Props>) => {
  const dispatch = useDispatch();
  const [initialFiltersLoaded, setInitialFiltersLoaded] = React.useState(false);
  const syncUrlFilters = useSyncFiltersSearchQuery();
  const { t } = useTranslation(['products', 'common']);
  const expandedFilters = useSelector(getProductsExpandedFiltersStates);
  const orderDetails = useSelector(getOrderDetails);
  const isSeller = useSelector(getIsSeller);
  const buyerIdFromCurrentOrder = useSelector(getBuyerIdFromCurrentOrder);
  const { trackGTMEvent } = useGTM();

  const getCurrentFiltersAndSort = useCurrentProductFiltersAndSort();
  const sort = useSelector(getCurrentOrDefaultProductSortingValue);
  const defaultSort = useSelector(getProductSortingDefaultValue);
  const filtersFromStore = useSelector(getProductFilters);
  const filters = React.useMemo(() => getOnlyNeccessaryFilters(getParsedToArrayFilters(filtersFromStore)), [filtersFromStore]);
  const isStockTypeSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const buyerId = isSeller ? buyerIdFromCurrentOrder : undefined;

  React.useEffect(() => {
    const { currentFilters, currentSort } = getCurrentFiltersAndSort();
    syncUrlFilters({ ...currentFilters }, currentSort === defaultSort ? undefined : sort);
    dispatch(updateFilters({ filters: currentFilters }));
    dispatch(setProductSortingValue(currentSort));
    dispatch(setExpandedProductFilters(Object.keys(currentFilters).reduce((acc, key) => ({ ...acc, [key]: true }), {})));
    setInitialFiltersLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeSort = React.useCallback(
    (newSortValue: string) => {
      syncUrlFilters({ ...filters }, newSortValue === defaultSort ? undefined : newSortValue);
      dispatch(setProductSortingValue(newSortValue));
    },
    [defaultSort, dispatch, filters, syncUrlFilters],
  );

  const clearFilters = () => {
    handleProductsFiltersChange({ ...(isStockTypeSeparationEnabled ? { stockType: filters.stockType } : {}) });
  };

  const handleProductsFiltersChange = React.useCallback(
    (newFilters: ProductFilters) => {
      syncUrlFilters(newFilters, sort === defaultSort ? undefined : sort);
      dispatch(updateFilters({ filters: newFilters }));
    },
    [syncUrlFilters, sort, defaultSort, dispatch],
  );

  const changeFilters = (newFilters: ProductFilters, preserveRest: boolean = true) => {
    const nonEmptyNewFilters = Object.entries(newFilters).reduce((acc, [key, value]) => {
      if (isEmpty(value as Array<string>) || !isDefined(value)) {
        return acc;
      }

      return { ...acc, [key]: value };
    }, {});

    if (!preserveRest) {
      handleProductsFiltersChange({ ...nonEmptyNewFilters });

      return;
    }

    const nonEmptyPreviousFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (key in newFilters || !isDefined(value)) {
        return acc;
      }

      return { ...acc, [key]: value };
    }, {});

    handleProductsFiltersChange({ ...nonEmptyPreviousFilters, ...nonEmptyNewFilters });
  };

  const { fetchNextPage, isFetching, isLoading, isPlaceholderData, isFetchingNextPage, data, isError, error, hasNextPage, failureCount } =
    useProducts({
      buyerId,
      filters,
      isEnabled: initialFiltersLoaded,
      orderId,
      sort,
    });
  const { data: productsEtaDates = [] } = useEtaDates({
    buyerId,
    deliveryWindowIds: filters.deliveryWindows ?? [],
    isEnabled: initialFiltersLoaded,
    search: filters.search ?? '',
  });

  React.useEffect(() => {
    if (!isError) {
      return;
    }

    if (failureCount > NUMBER_OF_FAILED_ATTEMPTS) {
      addToast(t('products:load_more_products_fail'), {
        actions: [
          {
            label: t('common:try_again'),
            onClick: () => dispatch(reloadPage()),
          },
        ],
        duration: 0,
        type: 'error',
      });
    }
  }, [dispatch, failureCount, fetchNextPage, isError, t]);

  const isLoadingProducts = (isFetching && isPlaceholderData) || isLoading;

  const isLoadingFilters = !isDefined(data);

  const { sortedProducts, pageCount, totalItems, productAttributes } = React.useMemo(
    () => ({
      pageCount: data?.currentPage ?? 0,
      productAttributes: data?.attributes ?? [],
      sortedProducts: data?.sortedProducts ?? [],
      totalItems: data?.totalItems ?? 0,
    }),
    [data],
  );

  React.useEffect(() => {
    if (!data?.lastPageProducts || isEmpty(data.lastPageProducts)) {
      return;
    }

    const globalIndexOffset = (data.currentPage - 1) * data.limit;
    const items = mapProductsToGAItems(data.lastPageProducts, { globalIndexOffset });
    trackGTMEvent('view_item_list', {
      ecommerce: {
        currency: orderDetails.currency.currency,
        item_list_id: GAProductListItemId.Selection,
        item_list_name: GAProductListItemName.Selection,
        items,
      },
    });
  }, [data?.currentPage, data?.lastPageProducts, data?.limit, orderDetails.currency.currency, orderDetails.order.order, trackGTMEvent]);

  React.useEffect(() => {
    const availabilityFilter = productAttributes.find(({ field }) => field === 'onlyAvailable');
    const isAvailableSoonEnabled = availabilityFilter?.values.find(value => value.filterValue === 'arrivingSoon');
    const hasAvailableSoonFilter = isDefined(filters.onlyAvailable) && filters.onlyAvailable === 'arrivingSoon';
    const shouldRemoveEtaFilters = !isAvailableSoonEnabled && hasAvailableSoonFilter;

    if (!shouldRemoveEtaFilters) {
      return;
    }

    handleProductsFiltersChange({ ...omit(['etaFrom', 'etaTo'], filters), onlyAvailable: 'no' });
  }, [productAttributes, filters, handleProductsFiltersChange]);

  React.useEffect(() => {
    const firstProductAttributeField = productAttributes[0]?.field;

    if (!isDefined(firstProductAttributeField) || isLoadingFilters) {
      return;
    }

    if (isEmpty(expandedFilters)) {
      dispatch(setExpandedProductFilters({ [firstProductAttributeField]: true }));
    }
  }, [dispatch, expandedFilters, productAttributes, isLoadingFilters]);

  const productsDetails: Record<string, Product.Full> = sortedProducts.reduce(
    (acc, item) => ({
      ...acc,
      [item.product]: item,
    }),
    {},
  );
  const hasProducts = !isEmpty(sortedProducts);
  const allProductsIds = sortedProducts.map(product => product.product);
  const shouldShowNoResults = initialFiltersLoaded && !hasProducts && !isLoadingProducts;

  return (
    <AllProductsContext.Provider
      value={{
        allProductsIds,
        changeFilters,
        changeSort,
        clearFilters,
        error,
        expandedFilters,
        fetchNextPage,
        filters,
        hasNextPage,
        isFetchingNextPage,
        isLoadingFilters,
        isLoadingProducts,
        pageCount,
        productAttributes,
        productsDetails,
        productsEtaDates,
        shouldShowNoResults,
        sort,
        sortedProducts,
        totalItems,
      }}
    >
      {children}
    </AllProductsContext.Provider>
  );
};
